/**
 * Seeding and dirty-checking the site settings form.
 *
 * The Settings pane has no edit MODE — it is always a form, and the Save bar
 * appears when something has actually changed. That removes a whole class of
 * bug: "Edit Site" used to sit outside the edit/view branch, so clicking it
 * while the form was open re-ran the seeder and threw away what had been typed.
 * A mode you can re-enter while you are already in it has no meaning.
 *
 * Always-on only works if "has this changed" is answered honestly, which is
 * what this module is for. Pure and dependency-free so jest can reach it —
 * react-router-dom v7 does not resolve under this jest config, so anything a
 * routed component imports has to live apart to be testable.
 */
import type {
  ManagedSite,
  ManagedSiteType,
  ManagedSiteUpdate,
  SiteSettings,
} from '../types';

/**
 * What the API sends back in place of a stored secret.
 *
 * `_encrypt_settings` masks it on read and `_merge_settings_secrets` swaps the
 * real value back in on write, so the sentinel is a round-trip token rather
 * than a value. Exported so the dirty check and the field that clears itself on
 * focus cannot drift into disagreeing about what it is — if they do, every
 * WordPress site opens with unsaved changes it never had.
 */
export const MASKED_SECRET = '********';

export type SiteFormDraft = {
  form: Partial<ManagedSiteUpdate>;
  settings: SiteSettings;
};

/** The scalar fields the form owns. Anything not here is not editable here. */
const FIELDS = [
  'name',
  'registered_name',
  'description',
  'registration_date',
  'site_type',
  'public_base_url',
  'require_review',
] as const;

export function getDefaultSettingsForType(siteType: ManagedSiteType): SiteSettings {
  switch (siteType) {
    case 'WORDPRESS':
      return { url: '', username: '', password: '' };
    case 'HUGO':
      return { hero_image_key: '', hero_alt_key: '' };
    case 'MANUAL':
      return { notes: '' };
    default:
      return null;
  }
}

/**
 * Seed the form from the saved site.
 *
 * Nulls become '' because that is what an input holds when empty — and the
 * dirty check has to undo the same coercion, or every site would open dirty on
 * the strength of a description nobody has ever written.
 */
export function siteFormFromSite(site: ManagedSite): SiteFormDraft {
  return {
    form: {
      name: site.name,
      registered_name: site.registered_name,
      description: site.description || '',
      registration_date: site.registration_date?.split('T')[0] || '',
      site_type: site.site_type,
      public_base_url: site.public_base_url || '',
      require_review: !!site.require_review,
    },
    settings:
      (site.settings as SiteSettings) || getDefaultSettingsForType(site.site_type),
  };
}

function settingsEqual(saved: SiteSettings, draft: SiteSettings): boolean {
  const a = (saved || {}) as Record<string, unknown>;
  const b = (draft || {}) as Record<string, unknown>;
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  for (const key of keys) {
    const before = a[key] ?? '';
    const after = b[key] ?? '';
    // The masked sentinel means "unchanged", not "the password is asterisks".
    // Without this every WordPress site is dirty the moment it is opened, the
    // Save bar is permanently on screen, and it stops meaning anything.
    if (after === MASKED_SECRET) continue;
    if (String(before) !== String(after)) return false;
  }
  return true;
}

/** Has anything actually changed since the form was seeded? */
export function isSiteFormDirty(
  site: ManagedSite,
  form: Partial<ManagedSiteUpdate>,
  settings: SiteSettings,
): boolean {
  const seeded = siteFormFromSite(site);
  for (const field of FIELDS) {
    const before = (seeded.form as Record<string, unknown>)[field] ?? '';
    const after = (form as Record<string, unknown>)[field] ?? '';
    if (String(before) !== String(after)) return true;
  }
  return !settingsEqual(seeded.settings, settings);
}

/**
 * The settings to submit for the type currently selected.
 *
 * Switching the type used to overwrite the settings object with the new type's
 * defaults, so WordPress -> Hugo -> WordPress silently discarded the url,
 * username and password. The drafts are kept per type instead and only the
 * selected one is sent; nothing is destroyed by looking at another option.
 */
export function settingsForType(
  drafts: Partial<Record<ManagedSiteType, SiteSettings>>,
  siteType: ManagedSiteType,
): SiteSettings {
  return drafts[siteType] ?? getDefaultSettingsForType(siteType);
}
